import React from "react";
import { displayStringForOrderStatus } from "../../../lib/formatters";
import { List, Button, Skeleton, Col, Row, Icon, Popover } from "antd";
import styled from "styled-components";
import { Order } from "../../../lib/model/Order";
import {
  OrderStatus,
  hasStatusReachedStatus,
  isStatusBeforeStatus
} from "../../../lib/model/OrderStatus";
import { OrderProgressIndicator } from "./OrderProgressIndicator";

const DataLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #061a32;
`;

const StyledRow = styled(Row)`
  width: 100%;
  padding: 30px;
  border: 1px solid #e8e8e8;
  margin-bottom: 15px;
  background-color: ${(props: { isRunning: boolean }) =>
    props.isRunning ? "#f7f7f7" : "#ffffff"};

  @media only screen and (max-width: 768px) {
    padding: 10px 5px;

    .ant-col.ant-col-8.ant-col-xs-24.ant-col-sm-24.ant-col-md-8 {
      border-right: none !important;
    }
    .ant-col.ant-col-16.ant-col-xs-24.ant-col-md-16 {
      margin-top: 15px;
    }
  }
`;

const StyledCol1 = styled(Col)`
  border-right: 1px solid #e8e8e8;
  height: 100%;
  padding: 0px 30px !important;

  @media only screen and (max-width: 576px) {
    border: none;
    padding: 0 10px !important;
  }
`;

const StyledCol2 = styled(Col)`
  padding: 0px 25px !important;
  word-break: break-word;

  @media only screen and (max-width: 576px) {
    text-align: center;
  }
`;

type Props = {
  isPlaceholder: boolean;
  order?: Order;
  onDetailsClick: (order: Order) => void;
};

export const OrderCell: React.FC<Props> = props => {
  const { order, isPlaceholder, onDetailsClick } = props;
  const isRunning = order
    ? hasStatusReachedStatus(order.status, OrderStatus.InProgress) &&
      isStatusBeforeStatus(order.status, OrderStatus.ReadyToInvoice)
    : false;
  return (
    <List.Item style={{ padding: 0 }} key={order && order.id}>
      <StyledRow type="flex" align="middle" isRunning={isRunning}>
        <Skeleton title={false} loading={isPlaceholder} active>
          {order && (
            <>
              <Col
                md={8}
                sm={24}
                xs={24}
                style={{ borderRight: "1px solid #e8e8e8", height: "100%" }}
              >
                <Row type="flex" align="middle" justify="center">
                  <Col md={13} sm={6} xs={10}>
                    <DataLabel>{order.internalReference || order.id}</DataLabel>
                    <br />
                    <div style={{ display: "flex" }}>
                      <Popover
                        content={
                          <div>
                            {order.startAddress.companyName}
                            <br />
                            {`${order.startAddress.street} ${order.startAddress.houseNumber}`}
                            <br />
                            {`${order.startAddress.zipCode} ${order.startAddress.city}`}
                          </div>
                        }
                      >
                        <DataLabel>{order.startAddress.zipCode}</DataLabel>
                      </Popover>
                      <DataLabel>&nbsp;→&nbsp;</DataLabel>
                      <Popover
                        content={
                          <div>
                            {order.endAddress.companyName} <br />
                            {`${order.endAddress.street} ${order.endAddress.houseNumber}`}
                            <br />
                            {`${order.endAddress.zipCode} ${order.endAddress.city}`}
                          </div>
                        }
                      >
                        <DataLabel>{order.endAddress.zipCode}</DataLabel>
                      </Popover>
                    </div>
                  </Col>
                  <Col md={11} sm={6} xs={8}>
                    <Button
                      type="primary"
                      style={{
                        borderRadius: 0,
                        height: 33,
                        backgroundColor: "#386ee8",
                        border: "none",
                        boxShadow: "0 3px 6px 0 rgba(0, 0, 0, 0.16)"
                      }}
                      onClick={() => onDetailsClick(order)}
                    >
                      Details <Icon type="right" />
                    </Button>
                  </Col>
                </Row>
              </Col>
              <Col md={16} sm={24} xs={24}>
                <Row type="flex" align="middle">
                  <StyledCol1 md={16} sm={16} xs={24}>
                    <OrderProgressIndicator status={order.status} />
                  </StyledCol1>
                  <StyledCol2 md={8} sm={8} xs={24}>
                    {displayStringForOrderStatus(order.status)}
                  </StyledCol2>
                </Row>
              </Col>
            </>
          )}
        </Skeleton>
      </StyledRow>
    </List.Item>
  );
};
